import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import SubTitle from '../common/SubTitle';
import Cards from '../common/Cards';
import SearchFilter from '../common/SearchFilter';
import axios from 'axios';
import config from '../../config.json';
import Update_Icon from "../../images/table/Update_icon.svg";
import Track_Icon from "../../images/table/Track_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DynamicTable from '../common/DynamicTable';
import DeleteModal from '../common/DeleteModal';
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import { useTranslation } from 'react-i18next';
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from '../../constants/styles';

// const headers = ['Stop ID', 'Stop Name', 'Routes', 'Actions'];
const headers = [
    { key: "Stop ID", value: 'Stop ID' },
    { key: "Stop Name", value: 'Stop Name' },
    { key: "Routes", value: 'Routes' },
    { key: ACTIONS_COLUMN_ID, value: 'Actions' }
];


const customActions = [
    {
        id: 'update',
        label: 'Update',
        icon: Update_Icon,
        path: '/stops/edit',
        requiredPermission: { path: '/api/stop', action: 'PUT' }
    },
    {
        id: 'view',
        label: 'View',
        icon: Track_Icon,
        path: '/stops/view',
        requiredPermission: { path: '/api/stop/id', action: 'GET' }
    },
    {
        id: 'delete',
        label: 'Delete',
        className: 'delete',
        icon: Delete_Icon,
        path: '',
        requiredPermission: { path: '/api/stop', action: 'DELETE' }
    }
];

export default function Stops() {
    const { t } = useTranslation()
    const [stopsData, setStopsData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        route: '',
        status: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedStopId, setSelectedStopId] = useState(null);
    const [selectedStopData, setSelectedStopData] = useState(null);
    const { state } = useUser();

    const navigate = useNavigate();
    const { hasPermission } = usePermission();

    // Enhanced permission check function
    const checkPermission = (requiredPath, requiredAction = null) => {
        // If no features found or no path required, allow access
        if (!state?.features || !requiredPath) {
            return true;
        }

        // Check if the user has the required permission
        return state.features.some(feature => {
            // Check if the API path matches
            const pathMatches = feature.api_path.includes(requiredPath);

            // If action is specified, check that too, otherwise just check the path
            return requiredAction ?
                (pathMatches && feature.action === requiredAction) :
                pathMatches;
        });
    };

    // Filter actions based on permissions
    const getPermittedActions = () => {
        return customActions.filter(action => {
            if (!action.requiredPermission) return true;
            const { path, action: requiredAction } = action.requiredPermission;
            return checkPermission(path, requiredAction);
        });
    };

    // Define your filters configuration
    const [filterConfig, setFilterConfig] = useState([
        {
            id: 'route',
            label: 'route',
            helperText: 'Select route',
            options: []
        }
    ]);

    // Function to fetch routes by coordinates
    const getRouteByStopCoordinates = async (lat, lng, orgId) => {
        try {
            const response = await axios.get(`${config.baseURL}/route/coordinates/`, {
                params: {
                    longitude: lng,
                    latitude: lat,
                    organizationId: orgId,
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching routes by coordinates:", error);
            return [];
        }
    };

    // Fetch stops data from API on component mount
    useEffect(() => {
        // Check if user has permission to view stops
        if (!checkPermission('/api/stop/organization', 'GET')) {
            console.log("No permission to view stops");
            navigate("/dashboard");
            return;
        }
        const fetchInitialData = async () => {
            try {
                setIsLoading(true);

                // Fetch stops
                const stopsResponse = await axios.get(
                    `${config.baseURL}/stop/organization`,
                    { withCredentials: true }
                );
                const stops = stopsResponse.data;

                // Fetch routes for each stop
                const stopsWithRoutes = await Promise.all(
                    stops.map(async (stop) => {
                        const routes = await getRouteByStopCoordinates(
                            stop.latitude,
                            stop.longitude,
                            stop.organizationId
                        );
                        return { ...stop, routes };
                    })
                );

                setStopsData(stopsWithRoutes);

                // Extract unique routes for filter
                const uniqueRoutes = [];
                const routeIds = new Set();

                stopsWithRoutes.forEach(stop => {
                    if (stop.routes && stop.routes.length > 0) {
                        stop.routes.forEach(route => {
                            const routeId = route.id || route.Id || route.ID;
                            const routeName = route.name || route.Name || `Route ${routeId}`;

                            if (routeId && !routeIds.has(routeId)) {
                                routeIds.add(routeId);
                                uniqueRoutes.push({
                                    value: routeId.toString(),
                                    label: routeName
                                });
                            }
                        });
                    }
                });

                // Update filter config with dynamic routes
                setFilterConfig(prev => {
                    const updated = [...prev];
                    const routeIndex = updated.findIndex(f => f.id === 'route');
                    if (routeIndex >= 0) {
                        updated[routeIndex].options = uniqueRoutes;
                    }
                    return updated;
                });

                setIsLoading(false);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    console.log("Unauthorized request");
                    navigate("/");
                    return;
                }
                console.error("Error fetching initial data:", error.response);
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [navigate]);

    const formatDataForTable = (data) => {
        return data.map((stop) => {
            // Determine route name
            let routesDisplay = 'No Route';

            if (stop.routes && stop.routes.length > 0) {
                routesDisplay = stop.routes.map(route => route.name || `Route ${route.id}`).join(', ');
            }

            // Create a formatted object for display
            return {
                'Stop ID': stop.stopId || 'N/A',
                'Stop Name': stop.name || 'N/A',
                'Routes': routesDisplay,
                // These properties aren't displayed but are used for action handling
                id: stop.id,
                originalData: stop // Store the complete original object
            };
        });
    };

    // Handle search changes
    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    // Handle filter changes
    const handleFilterChange = (filterValues) => {
        setFilters(filterValues);
    };

    // Filter data based on search query and filters
    const filteredStopsData = stopsData.filter((stop) => {
        // Check if route filter is applied
        if (filters.route) {
            const routeMatch = stop.routes && stop.routes.some(route =>
                String(route.id) === String(filters.route)
            );
            if (!routeMatch) return false;
        }

        // Check if status filter is applied
        if (filters.status && stop.status !== filters.status) {
            return false;
        }

        // If no search query, return true (show all that match filters)
        if (!searchQuery) {
            return true;
        }

        // Otherwise, search in all relevant fields
        const query = searchQuery.toLowerCase();
        return (
            (stop.stopId && stop.stopId.toLowerCase().includes(query)) ||
            (stop.name && stop.name.toLowerCase().includes(query)) ||
            (stop.routes && stop.routes.some(route =>
                route.name && route.name.toLowerCase().includes(query)
            ))
        );
    });



    console.log(filteredStopsData)

    // Handle view all stops on map
    const handleViewAllStopsOnMap = () => {
        // Check permission before navigating
        if (checkPermission('/api/stop/organization', 'GET')) {
            navigate("/stop/all/map", {
                state: { allStopsData: stopsData },
            });
        } else {
            console.log("No permission to view all stops on map");
        }
    };

    // Add stop handler
    const handleAddStop = () => {
        // Check permission before navigating
        if (checkPermission('/api/stop', 'POST')) {
            navigate("/stop/add");
        } else {
            console.log("No permission to add stop");
        }
    };


    // Handle delete dialog open
    const openDeleteModal = (stopId, stopData) => {
        // Check permission before showing delete modal
        if (checkPermission('/api/stop', 'DELETE')) {
            setSelectedStopId(stopId);
            setSelectedStopData(stopData);
            setIsDeleteModalOpen(true);
        } else {
            console.log("No permission to delete stops");
        }
    };

    // Handle delete dialog close
    const handleDeleteDialogClose = () => {
        setIsDeleteModalOpen(false);
        setSelectedStopId(null);
        setSelectedStopData(null);
    };

    // Handle delete action
    const handleDelete = async () => {
        if (selectedStopId && checkPermission('/api/stop', 'DELETE')) {
            try {
                const userState = localStorage.getItem('userState');
                const parsedUserState = JSON.parse(userState);
                const orgId = parsedUserState.user.organizationId

                await axios.delete(`${config.baseURL}/stop`, {
                    data: {
                        id: selectedStopId,
                        organizationId: orgId
                    },
                    withCredentials: true,
                });

                // Update local state
                const updatedStops = stopsData.filter(
                    (stop) => stop.id !== selectedStopId
                );
                setStopsData(updatedStops);
                handleDeleteDialogClose();
            } catch (error) {
                console.error("Error deleting stop:", error);
                handleDeleteDialogClose();
                // Add error handling here (e.g., show error toast)
            }
        }
    };

    // View stop details
    const handleViewStopDetails = (stopData) => {
        // Check permission before navigating
        if (checkPermission('/api/stop/id', 'GET')) {
            // Navigate to the view route with selected stop data
            navigate("/stop/track", {
                state: {
                    selectedStopData: stopData,
                    localStopsData: stopsData
                }
            });
        } else {
            console.log("No permission to view stop details");
        }
    };

    // Update stop details
    const handleUpdateStop = (stopId) => {
        // Check permission before navigating
        if (checkPermission('/api/stop', 'PUT')) {
            // Navigate to the edit route with stopId
            navigate(`/stop/${stopId}`);
        } else {
            console.log("No permission to update stop");
        }
    };

    const handleActionClick = (actionId, rowIndex, actionObj) => {
        // Get the formatted data for the current view
        const formattedData = formatDataForTable(filteredStopsData || []);

        // Use rowIndex to get the correct row from the formatted data
        const rowData = formattedData[rowIndex];

        if (!rowData) {
            console.error("Row data is undefined for index:", rowIndex);
            return;
        }

        // Get the stop ID and original data from the row
        const stopId = rowData.id;
        const stopData = rowData.originalData;

        if (!stopId || !stopData) {
            console.error("Missing stopId or stopData in row", rowData);
            return;
        }

        // Find the action from customActions to get its permission requirements
        const action = customActions.find(a => a.id === actionId);
        if (!action) {
            console.error("Action not found:", actionId);
            return;
        }

        // Check permission for this specific action
        if (action.requiredPermission) {
            const { path, action: requiredAction } = action.requiredPermission;
            if (!checkPermission(path, requiredAction)) {
                console.log(`No permission for action: ${actionId}`);
                return;
            }
        }
        // Update the selected stop state
        // setSelectedStopId(stopId);
        // setSelectedStopData(stopData);

        // Handle different action types
        switch (actionId) {
            case "update":
                handleUpdateStop(stopId);
                break;

            case "view":
                handleViewStopDetails(stopData);
                break;

            case "delete":
                openDeleteModal(stopId, stopData);
                break;

            default:
                break;
        }
    };

    // Get counts for cards
    const activeStopsCount = stopsData.filter(stop => stop.status === "ACTIVE").length || 0;
    const totalRoutesCount = new Set(
        stopsData.flatMap(stop => stop.routes ? stop.routes.map(route => route.id) : [])
    ).size;

    // Debug logs
    console.log("Filtered stops data:", filteredStopsData);
    console.log("Formatted data for table:", formatDataForTable(filteredStopsData || []));
    // Show add button only if user has permission
    const showAddButton = checkPermission('/api/stop', 'POST');

    // Show track on map button only if user has permission
    const showTrackButton = checkPermission('/api/stop/organization', 'GET');

    return (
        <>
            <SubTitle
                title="Stops"
                path="organization > Stops"
                add={showAddButton ? "createStop" : null}
                addPath="/stop/add"
                track={showTrackButton ? "viewStopsOnMap" : null}
                trackPath="/stops/all/map"
                onAddClick={handleAddStop}
                onTrackClick={handleViewAllStopsOnMap}
            />

            <Cards cardData={[
                {
                    title: "totalStops",
                    value: stopsData?.length || 0,
                    backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
                    titleColor: TEXT_COLORS.WHITE,
                    valueColor: TEXT_COLORS.WHITE
                },
                {
                    title: "connectedRoutes",
                    value: totalRoutesCount,
                    backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
                    titleColor: TEXT_COLORS.WHITE,
                    valueColor: TEXT_COLORS.WHITE
                },

            ]} />

            <div className="table_container">
                <SearchFilter
                    filters={filterConfig}
                    searchPlaceholder="search"
                    onSearchChange={handleSearchChange}
                    onFilterChange={handleFilterChange}
                />

                <Suspense fallback={<div className="loader"></div>}>
                    <DynamicTable
                        headers={headers}
                        data={formatDataForTable(filteredStopsData || [])}
                        onActionClick={handleActionClick}
                        actions={getPermittedActions()}
                        loading={isLoading}
                    />
                </Suspense>
            </div>

            {/* Delete Confirmation Dialog */}
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={handleDeleteDialogClose}
                onConfirm={handleDelete}
                title="Delete Stop"
                message={
                    <>
                        {t("Are you sure you want to delete")}{' '}
                        <span style={{ fontWeight: 600, color: "#d32f2f" }}>
                            {selectedStopData?.name || t("this stop")}
                        </span>
                        ? {t("This action cannot be undone.")}
                    </>
                }
            />

        </>
    );
}

